import type { ScreenBackground, ScreenLayout } from "./types/screen";
import type { EditorState } from "./state";

// WEB-2552 (пачка 49, заход Р0): три помощника фона экрана вынесены из
// editorSlice.ts («перенос токен в токен») — withLayoutKey/withBackgroundKey/
// withRenderableImageMode.

/**
 * WEB-927 атом 4: иммутабельно проставить/снять одну ось раскладки.
 * Снятие оси удаляет ключ, а опустевший layout возвращается как undefined —
 * «не задано» должно оставаться отсутствием, иначе дефолт стиль-пака
 * на этом экране больше никогда не применится.
 */
export function withLayoutKey(
  layout: ScreenLayout | undefined,
  key: "alignY" | "alignX",
  value: string | undefined,
): ScreenLayout | undefined {
  const rest: ScreenLayout = { ...(layout ?? {}) };
  delete rest[key];
  if (value === undefined) {
    return Object.keys(rest).length > 0 ? rest : undefined;
  }
  return { ...rest, [key]: value } as ScreenLayout;
}

/**
 * WEB-1805, ревизия (М11): иммутабельно проставить/снять ОДНО поле фона
 * экрана. Опустевший фон возвращается как undefined — ровно тот же инвариант,
 * что у `withLayoutKey`: «не задано» обязано оставаться ОТСУТСТВИЕМ поля.
 *
 * Почему это не косметика. Пустой `background: {}` неотличим от настроенного
 * фона по наличию ключа, уезжает на сервер как «изменение» и делает проверки
 * вида «у экрана есть свой фон?» ложно-положительными. До ревизии сброс
 * ЕДИНСТВЕННОГО поля (цвета, градиента, картинки, вуали) оставлял именно его.
 */
export function withBackgroundKey<K extends keyof ScreenBackground>(
  background: ScreenBackground | undefined,
  key: K,
  value: ScreenBackground[K] | undefined,
): ScreenBackground | undefined {
  const rest: ScreenBackground = { ...(background ?? {}) };
  delete rest[key];
  if (value === undefined) {
    return Object.keys(rest).length > 0 ? rest : undefined;
  }
  return { ...rest, [key]: value };
}

/**
 * WEB-1873: картинка фона обязана попасть в тот режим, который поверхность
 * РИСУЕТ. Второй конец правила — точка монтирования общей обёртки фона в
 * плеере воронки (`QuizScreenWizard`: `<ScreenBackgroundWrapper
 * appliesTo="full-screen">`): карточный режим воронка не рисует сознательно
 * (решение WEB-927 — включить его задним числом значило бы проявить картинки
 * на уже опубликованных воронках). А отсутствие `mode` резолвер читает именно
 * как 'card' (`resolveScreenLayout`). Пока панель писала один `imageUrl`,
 * оба конца расходились молча: клиент платил 10 кредитов за генерацию, адрес
 * ложился в контент и доезжал до публичного ответа, а рисовать его было
 * некому — ни в превью, ни на опубликованной странице.
 *
 * Правило узкое намеренно:
 *  • только редактор ВОРОНКИ — ОБЕИМИ ветками, и постановкой, и снятием
 *    (ревизия пачки 7, Н1). Экран оплаты рисует оба режима (его обёртка
 *    смонтирована без `appliesTo`), дефекта там нет, а смена умолчания
 *    поменяла бы вид фона, который автор пейволла ставит сегодня. Там же
 *    'full-screen' — НАСТОЯЩИЙ выбор автора, и уносить его заодно с картинкой
 *    нельзя: снял адрес → поставил снова → фон молча уехал «карточкой».
 *    Правило убирает только тот режим, который само же и поставило;
 *  • в воронке — только постановка НЕПУСТОЙ картинки. При снятии картинки
 *    режим уходит вместе с ней: режим САМ ПО СЕБЕ фоном не является (без
 *    картинки он не рисует ничего ни на одной поверхности), а оставленный
 *    объект `{mode:'full-screen'}` сломал бы инвариант «не задано = нет ключа» —
 *    пустой фон неотличим от настроенного и врёт проверкам «у экрана есть свой
 *    фон?» (шрам WEB-1805/М11). Ставим и снимаем пару одним действием. На
 *    пейволле инвариант М11 держит сам `withBackgroundKey`: если режима автор
 *    не выбирал, единственный `imageUrl` уходит и фон становится undefined;
 *  • правится ТОЛЬКО содержимое действия автора. Ни один уже сохранённый экран
 *    от этого не перерисовывается — вид выкаченных воронок не меняется.
 *
 * Явный выбор «Карточкой в потоке» (`setScreenBackgroundMode`) правило не
 * перебивает: там автор говорит про режим, здесь — про картинку.
 */
export function withRenderableImageMode(
  background: ScreenBackground | undefined,
  editorType: EditorState["editorType"],
  hasImage: boolean,
): ScreenBackground | undefined {
  if (!background) return background;
  // Ревизия пачки 7 (Б7): на ПЕЙВОЛЛЕ ранний выход стоит ДО ветки снятия,
  // поэтому одинокий { mode: 'full-screen' } переживает удаление картинки —
  // это выбор автора, а не след правила (правило его не ставило). Инвариант
  // М11 «пустой фон = нет ключа» там держит withBackgroundKey только для
  // фона БЕЗ выбранного режима; вреда нет — все потребители гейтятся на
  // imageUrl (resolveScreenLayout, обёртка рисует по hasBackgroundImage).
  if (editorType !== "quiz") return background;
  if (!hasImage) {
    const keys = Object.keys(background);
    return keys.length === 1 && keys[0] === "mode" ? undefined : background;
  }
  if (background.mode === "full-screen") return background;
  return { ...background, mode: "full-screen" };
}
